import type { SignInIdentifier } from '@logto/schemas';
import { useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import CowcamoTextLink from '@/components/CowcamoTextLink';
import { EXTERNAL_PASSWORD_RESET_PATH } from '@/constants/external-pages';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import { UserFlow } from '@/types';
import { getSiteOrigin } from '@/utils/site-origin';

type Props = {
  readonly identifier?: SignInIdentifier;
  readonly value?: string;
  readonly className?: string;
};

/**
 * When the deployment redirects password reset to an external host instead of
 * handling it in-experience, the reset URL is built from `${siteOrigin}${EXTERNAL_PASSWORD_RESET_PATH}`.
 * The origin comes from {@link getSiteOrigin} (the cowcamo site, derived from the per-environment
 * SIE terms/privacy URL), and the path is centralized in `@/constants/external-pages`.
 */
const ForgotPasswordLink = ({ className, ...identifierData }: Props) => {
  const navigate = useNavigateWithPreservedSearchParams();
  const { setForgotPasswordIdentifierInputValue } = useContext(UserInteractionContext);
  const { experienceSettings } = useContext(PageContext);
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();

  const siteOrigin = getSiteOrigin(experienceSettings);
  const externalResetUrl = siteOrigin
    ? new URL(EXTERNAL_PASSWORD_RESET_PATH, siteOrigin).href
    : undefined;

  // Redirect to the external reset page when an origin (terms/privacy host) is configured.
  // Opens in the same tab (no `target="_blank"`): the reset page is a full-page destination,
  // not a reference the user needs side-by-side with the sign-in screen.
  if (externalResetUrl) {
    return (
      <CowcamoTextLink
        className={className}
        text="action.forgot_password"
        href={externalResetUrl}
      />
    );
  }

  // Upstream default: in-experience forgot-password flow, only when enabled in SIE.
  if (!isForgotPasswordEnabled) {
    return null;
  }

  return (
    <CowcamoTextLink
      className={className}
      text="action.forgot_password"
      onClick={() => {
        setForgotPasswordIdentifierInputValue({
          type: identifierData.identifier,
          value: identifierData.value ?? '',
        });

        navigate(`/${UserFlow.ForgotPassword}`);
      }}
    />
  );
};

export default ForgotPasswordLink;
